'use client';
import React, { useState } from 'react';
import { CheckCircle, XCircle, ExternalLink, Info, Star, Trophy, Shield, TrendingUp, Building2 } from 'lucide-react';
import {
  INSURERS,
  formatCurrency,
  formatPremium,
  getCSRBadgeClass,
  getCSRLabel,
  type Policy,
} from '@/lib/policyData';
import DisclaimerPopup from '@/components/ui/DisclaimerPopup';

interface ComparisonTableClientProps {
  policies: Policy[];
}

function BoolFeature({ label, value, tooltip }: { label: string; value: boolean; tooltip?: string }) {
  const [showTip, setShowTip] = useState(false);
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 py-2 border-b border-border/40 last:border-b-0">
      <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0" />
      <span className="text-sm text-foreground">{label}</span>
      {tooltip && (
        <div className="relative flex-shrink-0 ml-auto">
          <button
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
            className="text-muted-foreground hover:text-foreground"
            aria-label={`Info: ${label}`}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
          {showTip && (
            <div className="absolute right-5 top-0 z-20 w-44 bg-foreground text-card text-xs rounded-lg px-2 py-2 shadow-xl pointer-events-none">
              {tooltip}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : score >= 60 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-red-700 bg-red-50 border-red-200';
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold border ${color}`}>
      <Star className="w-3.5 h-3.5 fill-current" />
      {score}/100 Match Score
    </span>
  );
}

function getBestPremium(policies: Policy[]): number {
  return Math.min(...policies.map((p) => p.premium));
}
function getBestCSR(policies: Policy[]): number {
  return Math.max(...policies.map((p) => p.claimSettlementRatio));
}
function getBestCoverage(policies: Policy[]): number {
  return Math.max(...policies.map((p) => p.coverageAmount));
}
function getBestNetwork(policies: Policy[]): number {
  return Math.max(...policies.map((p) => p.networkHospitals));
}
function getBestWaiting(policies: Policy[]): number {
  return Math.min(...policies.map((p) => p.waitingPeriodYears));
}

export default function ComparisonTableClient({ policies }: ComparisonTableClientProps) {
  const [disclaimerState, setDisclaimerState] = useState<{ open: boolean; buyUrl: string; insurerName: string }>({
    open: false,
    buyUrl: '',
    insurerName: '',
  });

  const bestPremium = getBestPremium(policies);
  const bestCSR = getBestCSR(policies);
  const bestCoverage = getBestCoverage(policies);
  const bestNetwork = getBestNetwork(policies);
  const bestWaiting = getBestWaiting(policies);

  const overallScores = policies.map((p) => {
    let score = 0;
    if (p.premium === bestPremium) score += 20;
    if (p.claimSettlementRatio === bestCSR) score += 25;
    if (p.coverageAmount === bestCoverage) score += 20;
    if (p.networkHospitals === bestNetwork) score += 15;
    if (p.waitingPeriodYears === bestWaiting) score += 10;
    if (p.restoration) score += 5;
    if (p.maternity) score += 3;
    if (p.opd) score += 2;
    return Math.min(score, 100);
  });

  const bestPlanIndex = overallScores.indexOf(Math.max(...overallScores));
  const bestPolicy = policies[bestPlanIndex];
  const bestScore = overallScores[bestPlanIndex];
  const ins = INSURERS[bestPolicy.insurer];

  return (
    <>
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Best Plan Header */}
        <div className="bg-card border-2 border-primary rounded-2xl overflow-hidden card-shadow">
          {/* Banner */}
          <div className="bg-primary px-5 py-3 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary-foreground" />
            <span className="text-primary-foreground font-bold text-sm tracking-wide uppercase">Best Plan Recommendation</span>
          </div>

          {/* Plan Identity */}
          <div className="px-5 pt-5 pb-4 flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-extrabold text-lg flex-shrink-0 ${ins.bgColor} ${ins.color}`}>
              {ins.abbr}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground font-medium">{ins.name}</p>
              <h2 className="text-xl font-extrabold text-foreground leading-tight">{bestPolicy.planName}</h2>
              <div className="mt-1.5">
                <ScoreBadge score={bestScore} />
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-t border-border">
            <div className="px-4 py-4 border-r border-border text-center">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="text-2xs font-semibold uppercase tracking-wide">Premium</span>
              </div>
              <p className="text-lg font-extrabold text-primary font-tabular">{formatPremium(bestPolicy.premium)}</p>
              <p className="text-2xs text-muted-foreground">per year</p>
            </div>
            <div className="px-4 py-4 border-r border-border sm:border-r text-center">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                <Shield className="w-3.5 h-3.5" />
                <span className="text-2xs font-semibold uppercase tracking-wide">Cover</span>
              </div>
              <p className="text-lg font-extrabold text-foreground font-tabular">{formatCurrency(bestPolicy.coverageAmount)}</p>
              <p className="text-2xs text-muted-foreground">sum insured</p>
            </div>
            <div className="px-4 py-4 border-r border-border text-center border-t sm:border-t-0">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                <Star className="w-3.5 h-3.5" />
                <span className="text-2xs font-semibold uppercase tracking-wide">CSR</span>
              </div>
              <p className="text-lg font-extrabold text-foreground font-tabular">{bestPolicy.claimSettlementRatio}%</p>
              <span className={`text-2xs px-2 py-0.5 rounded-full font-semibold ${getCSRBadgeClass(bestPolicy.claimSettlementRatio)}`}>
                {getCSRLabel(bestPolicy.claimSettlementRatio)}
              </span>
            </div>
            <div className="px-4 py-4 text-center border-t sm:border-t-0">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                <Building2 className="w-3.5 h-3.5" />
                <span className="text-2xs font-semibold uppercase tracking-wide">Network</span>
              </div>
              <p className="text-lg font-extrabold text-foreground font-tabular">
                {bestPolicy.networkHospitals >= 1000
                  ? `${(bestPolicy.networkHospitals / 1000).toFixed(0)}K+`
                  : bestPolicy.networkHospitals}
              </p>
              <p className="text-2xs text-muted-foreground">hospitals</p>
            </div>
          </div>

          {/* Buy CTA */}
          <div className="px-5 py-4 bg-primary/5 border-t border-border flex flex-col sm:flex-row items-center gap-3">
            <button
              onClick={() => setDisclaimerState({ open: true, buyUrl: bestPolicy.buyUrl, insurerName: ins.name })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-sm font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all shadow-md shadow-primary/25"
            >
              Buy Now — {formatPremium(bestPolicy.premium)}/yr
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
            </button>
            <p className="text-xs text-muted-foreground text-center sm:text-left">
              You will be redirected to {ins.name}&apos;s official website
            </p>
          </div>
        </div>

        {/* Plan Details */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden card-shadow">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-bold text-foreground">Plan Details</h3>
          </div>
          <div className="px-5 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <div>
              <div className="flex items-center justify-between py-2.5 border-b border-border/40">
                <span className="text-sm text-muted-foreground">Policy Type</span>
                <span className="text-sm font-semibold text-foreground">{bestPolicy.policyType}</span>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-border/40">
                <span className="text-sm text-muted-foreground">Room Rent Limit</span>
                <span className="text-sm font-semibold text-foreground">{bestPolicy.roomRentLimit}</span>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-border/40">
                <span className="text-sm text-muted-foreground">Co-Payment</span>
                <span className="text-sm font-semibold text-foreground">{bestPolicy.coPayment}</span>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-border/40 sm:border-b-0">
                <span className="text-sm text-muted-foreground">Daycare Procedures</span>
                <span className="text-sm font-semibold text-foreground">
                  {bestPolicy.daycareProceduress > 0 ? `${bestPolicy.daycareProceduress}+` : 'N/A'}
                </span>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between py-2.5 border-b border-border/40">
                <span className="text-sm text-muted-foreground">Waiting Period</span>
                <span className="text-sm font-semibold text-foreground">
                  {bestPolicy.waitingPeriodYears} year{bestPolicy.waitingPeriodYears !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-muted-foreground">Cashless Available</span>
                <span>
                  {bestPolicy.cashlessAvailable
                    ? <CheckCircle className="w-4 h-4 text-[var(--success)]" />
                    : <XCircle className="w-4 h-4 text-[var(--danger)]" />}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Included Benefits */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden card-shadow">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-bold text-foreground">Included Benefits</h3>
          </div>
          <div className="px-5 py-2">
            <BoolFeature label="Restoration Benefit" value={bestPolicy.restoration} tooltip="Sum insured is restored if exhausted during the year" />
            <BoolFeature label="Maternity Cover" value={bestPolicy.maternity} />
            <BoolFeature label="OPD Consultation" value={bestPolicy.opd} tooltip="Out-patient department visits covered" />
            <BoolFeature label="Critical Illness Rider" value={bestPolicy.criticalIllness} />
            <BoolFeature label="Pre-existing Cover" value={bestPolicy.preExistingCover} />
            <BoolFeature label="Ambulance Cover" value={bestPolicy.ambulanceCover} />
            <BoolFeature label="Annual Health Checkup" value={bestPolicy.annualHealthCheckup} />
            <BoolFeature label="Section 80D Tax Benefit" value={bestPolicy.taxBenefit80D} tooltip="Tax deduction up to ₹25,000 (₹50,000 for senior citizens)" />
            <BoolFeature label="No Claim Bonus" value={bestPolicy.noClaimBonus} tooltip="Sum insured increases on claim-free years" />
          </div>
        </div>

        {/* Why This Plan */}
        {policies.length > 1 && (
          <div className="bg-primary/5 border border-primary/20 rounded-2xl px-5 py-4">
            <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-primary" />
              Why this plan wins
            </h3>
            <ul className="space-y-1.5">
              {bestPolicy.premium === bestPremium && (
                <li className="text-sm text-foreground flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0" />
                  Lowest annual premium among compared plans
                </li>
              )}
              {bestPolicy.claimSettlementRatio === bestCSR && (
                <li className="text-sm text-foreground flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0" />
                  Highest claim settlement ratio ({bestPolicy.claimSettlementRatio}%)
                </li>
              )}
              {bestPolicy.coverageAmount === bestCoverage && (
                <li className="text-sm text-foreground flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0" />
                  Maximum sum insured ({formatCurrency(bestPolicy.coverageAmount)})
                </li>
              )}
              {bestPolicy.networkHospitals === bestNetwork && (
                <li className="text-sm text-foreground flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0" />
                  Largest cashless hospital network
                </li>
              )}
              {bestPolicy.waitingPeriodYears === bestWaiting && (
                <li className="text-sm text-foreground flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--success)] flex-shrink-0" />
                  Shortest waiting period ({bestPolicy.waitingPeriodYears} year{bestPolicy.waitingPeriodYears !== 1 ? 's' : ''})
                </li>
              )}
            </ul>
          </div>
        )}

        {/* IRDAI disclaimer */}
        <div className="px-4 py-4 bg-amber-50 border border-amber-200 rounded-2xl">
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Disclaimer:</strong> Comparison data is sourced from publicly available IRDAI filings and insurer brochures (2023–24). CSR figures are from IRDAI Annual Report 2023–24. Verify all policy terms with the insurer before purchasing. MediCompare is an IRDAI-registered web aggregator.
          </p>
        </div>
      </div>

      <DisclaimerPopup
        isOpen={disclaimerState.open}
        onClose={() => setDisclaimerState((prev) => ({ ...prev, open: false }))}
        buyUrl={disclaimerState.buyUrl}
        insurerName={disclaimerState.insurerName}
      />
    </>
  );
}